import { useEffect, useState, useCallback } from 'react';
import MetaTags from 'react-meta-tags';
import Breadcrumbs from '../../../../components/Common/Breadcrumb';
import { useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import FeatherIcon from 'feather-icons-react';
import { InspectionTrackerListTable } from './Components/InspectionTrackerListTable';
import { PropertySelector } from './Components/PropertySelector';
import { InspectionTypesTable } from './Components/InspectionTypesTable';
import { InspectionFrequenciesTable } from './Components/InspectionFrequenciesTable';
import AutomatedEmailsSettings from '../../AutomatedEmails/AutomatedEmailsSettings';
import { getPropertyList } from '../../../Inventory/pages/FacilityInventory/Helpers/Api';
import {
  createInspection,
  updateInspection,
  getInspectionSchedule,
  getInspectionYears,
  exportInspectionsToExcel,
  getInspectionTypes,
  getInspectionFrequencies,
} from './Components/FormikHelpers/Api';
import { setLoading } from '../../../../store/actions';

const TABS = {
  TRACKER: 'tracker',
  TYPES: 'types',
  FREQUENCIES: 'frequencies',
  REMINDERS: 'reminders',
};

const InspectionTracker = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(TABS.TRACKER);
  const [properties, setProperties] = useState([]);
  const currentYear = new Date().getFullYear();
  const [rows, setRows] = useState([]);
  const [years, setYears] = useState([currentYear - 1, currentYear, currentYear + 1]);
  const [user, setUser] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [frequencyOptions, setFrequencyOptions] = useState([]);
  const [filters, setFilters] = useState({
    property_id: '',
    building_name: '',
    property_type: '',
    year: currentYear,
    overdueOnly: false,
    frequencyId: '',
  });

  const getUserFromToken = useCallback(() => {
    try {
      const token = localStorage.getItem('accessToken');
      return token ? jwt_decode(token) : null;
    } catch { return null; }
  }, []);

  const fetchProperties = useCallback(async () => {
    const currentUser = getUserFromToken();
    if (!currentUser) return;
    setUser(currentUser);
    try {
      dispatch(setLoading(true));
      const result = await getPropertyList(currentUser?.role || '', currentUser?.id || '');
      setProperties(result?.propertyList || []);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      Swal.fire('Error', 'Failed to load properties', 'error');
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, getUserFromToken]);

  const fetchSelectOptions = useCallback(async () => {
    try {
      const [typesRes, freqsRes] = await Promise.all([
        getInspectionTypes({ activeOnly: 'true' }),
        getInspectionFrequencies({ activeOnly: 'true' }),
      ]);
      setTypeOptions(
        (typesRes?.data || []).map((t) => ({
          value: t._id,
          label: t.name,
          defaultContractor: t.defaultContractor,
        }))
      );
      setFrequencyOptions(
        (freqsRes?.data || []).map((f) => ({
          value: f._id,
          label: f.name,
          days: f.days,
        }))
      );
    } catch (err) {
      console.error('Failed to fetch select options:', err);
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    if (!filters.property_id) {
      setRows([]);
      return;
    }
    try {
      dispatch(setLoading(true));
      const response = await getInspectionSchedule({
        propertyId: filters.property_id,
        buildingName: filters.building_name || undefined,
        year: filters.year,
        overdueOnly: filters.overdueOnly || undefined,
        frequencyId: filters.frequencyId || undefined,
      });
      setRows(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch the inspection schedule:', error);
      setRows([]);
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, filters.property_id, filters.building_name, filters.year, filters.overdueOnly, filters.frequencyId]);

  // The years on offer: from the building's first record through next year.
  useEffect(() => {
    if (!filters.property_id) return undefined;
    let live = true;
    getInspectionYears({ propertyId: filters.property_id, buildingName: filters.building_name || undefined }).then((list) => {
      if (!live || !list.length) return;
      setYears(list.includes(filters.year) ? list : [...list, filters.year].sort((x, y) => x - y));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.property_id, filters.building_name]);

  // Create adds lines to the building's record; update saves the record. The
  // server asks before a second schedule of a type the building already has.
  const handleSaveRow = useCallback(
    async (rowData) => {
      const { _isNew: _ignored, _id, ...payload } = rowData;
      const save = (extra) => (_id ? updateInspection(_id, { ...payload, ...extra }) : createInspection({ ...payload, ...extra }));
      try {
        dispatch(setLoading(true));
        let response;
        try {
          response = await save({});
        } catch (error) {
          if (!error?.duplicateType) throw error;
          dispatch(setLoading(false));
          const answer = await Swal.fire({
            title: 'Add a second schedule?',
            text: error.message,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#1A6B4A',
            confirmButtonText: 'Add it',
            cancelButtonText: 'Back',
            reverseButtons: true,
          });
          if (!answer.isConfirmed) return null;
          dispatch(setLoading(true));
          response = await save({ allowDuplicateType: true });
        }
        await fetchSchedule();
        return { success: true, data: response?.data || response };
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, fetchSchedule]
  );

  const handleExportExcel = () => {
    if (!filters.property_id) {
      toast.warn('Please select a property first');
      return;
    }
    exportInspectionsToExcel({
      propertyId: filters.property_id,
      buildingName: filters.building_name || undefined,
      year: filters.year,
      overdueOnly: filters.overdueOnly || undefined,
    });
  };

  useEffect(() => { fetchProperties(); }, [fetchProperties]);
  useEffect(() => { fetchSchedule(); }, [fetchSchedule]);
  useEffect(() => { fetchSelectOptions(); }, [fetchSelectOptions]);
  const refreshSelectOptions = useCallback(() => {
    fetchSelectOptions();
  }, [fetchSelectOptions]);

  const tabStyle = (tab) => ({
    cursor: 'pointer',
    padding: '8px 20px',
    fontWeight: 700,
    fontSize: '0.85rem',
    color: activeTab === tab ? '#1A6B4A' : '#6B8F7C',
    borderBottom: activeTab === tab ? '2px solid #1A6B4A' : '2px solid transparent',
    background: 'none',
    border: 'none',
    borderBottom: activeTab === tab ? '2px solid #1A6B4A' : '2px solid transparent',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    transition: 'all 0.15s ease',
  });

  return (
    <div className="page-content">
      <MetaTags>
        <title>Rentdigicare | Inspection Tracker</title>
      </MetaTags>
      <div className="container-fluid">
        <Breadcrumbs title="Maintenance" breadcrumbItem="Inspection Tracker" />

        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24, borderBottom: '1px solid #D4E8DC' }}>
          <button style={tabStyle(TABS.TRACKER)} onClick={() => setActiveTab(TABS.TRACKER)}>
            <FeatherIcon icon="clipboard" size={14} />
            Tracker
          </button>
          <button style={tabStyle(TABS.TYPES)} onClick={() => setActiveTab(TABS.TYPES)}>
            <FeatherIcon icon="tag" size={14} />
            Inspection Types
          </button>
          <button style={tabStyle(TABS.FREQUENCIES)} onClick={() => setActiveTab(TABS.FREQUENCIES)}>
            <FeatherIcon icon="clock" size={14} />
            Frequencies
          </button>
          {(user?.role === 'company' || user?.role === 'auditor') && (
            <button style={tabStyle(TABS.REMINDERS)} onClick={() => setActiveTab(TABS.REMINDERS)}>
              <FeatherIcon icon="bell" size={14} />
              Reminders &amp; Reports
            </button>
          )}
        </div>

        {activeTab === TABS.TRACKER && (
          <>
            <PropertySelector
              propertyList={properties}
              selectedFilters={filters}
              onPropertySelectionChange={setFilters}
              onExportExcel={handleExportExcel}
              frequencyOptions={frequencyOptions}
              yearOptions={years}
            />
            <InspectionTrackerListTable
              rows={rows}
              year={filters.year}
              properties={properties}
              filters={filters}
              onSaveRow={handleSaveRow}
              onRefresh={fetchSchedule}
              userRole={user?.role || 'other'}
              userLead={!!user?.lead}
              typeOptions={typeOptions}
              frequencyOptions={frequencyOptions}
            />
          </>
        )}
        {activeTab === TABS.TYPES && (<InspectionTypesTable onRefresh={refreshSelectOptions} />)}
        {activeTab === TABS.FREQUENCIES && (<InspectionFrequenciesTable onRefresh={refreshSelectOptions} />)}
        {activeTab === TABS.REMINDERS && (
          <AutomatedEmailsSettings
            sections={['inspectionReminders', 'inspectionWeeklyDigest', 'inspectionMonthlyReport', 'serviceReminders']}
            readOnly={user?.role !== 'company'}
            compact
          />
        )}
      </div>
    </div>
  );
};

export { InspectionTracker };